import {
  ChatStatus,
  RecordingStatus,
  RESEARCH_CHAT_MODE,
  ULTRA_CHAT_MODE,
  config,
  useStores,
} from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import mergeRefs from 'merge-refs';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { RegenerateButton } from '@/components/blocks/Chat/RegenerateButton';
import { FileGallery } from '@/components/blocks/FileGallery';
import { ChatModeDropdown } from '@/components/blocks/Thread/ChatModeButtons';
import { ComposerAddMenu } from '@/components/blocks/Thread/ComposerAddMenu';
import { LegacyChatWarning } from '@/components/blocks/Thread/LegacyChatWarning';
import { MicrophoneButton } from '@/components/blocks/Thread/MicrophoneButton';
import { QueuedMessages } from '@/components/blocks/Thread/QueuedMessages';
import { SendButton } from '@/components/blocks/Thread/SendButton';
import { useComposer } from '@/components/blocks/Thread/useComposer';
import { useFocusComposerOnType } from '@/components/blocks/Thread/useFocusComposerOnType';
import { VoiceModeButton } from '@/components/blocks/Thread/VoiceModeButton';
import { VoiceModeWaveform } from '@/components/blocks/Thread/VoiceModeWaveform';
import { IconButton, Tap, YStack } from '@/components/ui';
import {
  useCurrentChatMode,
  useChatModeLimitAutoSwitch,
} from '@/features/chat/chat.hooks';
import { getVoiceModeMediaStream } from '@/features/chat/voice/voice-mode-desktop-platform';
import { HivemindPicker } from '@/features/hivemind';
import { useShortcutKeys } from '@/features/shortcuts';
import {
  useSubscription,
  LiteModeBanner,
  ResearchLimitWarning,
  OutOfCreditsComposerBanner,
  UsageWarningNotification,
} from '@/features/subscription';
import { useHashRoute } from '@/hooks/useHashRoute';
import { useScrollFade } from '@/hooks/useScrollFade';
import { useScrollToBottomOnGrow } from '@/hooks/useScrollToBottomOnGrow';
import { userStore } from '@/stores/user.store';
import { Routes } from '@/types/routes';
import { trackEvent } from '@/utils/analytics';
import { classList } from '@/utils/cn';
import { NEW_CHAT_ID } from '@/utils/constants';

import { AgentsComposerEditor } from './agents-composer-editor';
import { agentsStore } from './agents.store';

const AGENT_COMPOSER_MIN_HEIGHT = 28;

type AgentComposerProps = {
  threadId: string;
  setIsDraggingFile: (isDraggingFile: boolean) => void;
  sendAskUserAnswersRef: MutableRefObject<() => void>;
};

export const AgentComposer = observer((props: AgentComposerProps) => {
  const { drafts: draftsStore, messages: messagesStore } = useStores();
  const { currentChatMode, setCurrentChatMode } = useCurrentChatMode();
  const { setHash } = useHashRoute();
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const flipTransition = shouldReduceMotion
    ? { duration: 0 }
    : ({ type: 'spring', stiffness: 650, damping: 45 } as const);
  useChatModeLimitAutoSwitch();

  const {
    thread,
    threadId,
    draftId,
    resendQuery,
    draft,
    inputRef,
    mentionsRef,
    blurInputHotkeyRef,
    setDraftContent,
    handlePaste,
    handleFileSelection,
    handleSavePastedText,
    handleInsertPastedText,
    isBusy,
    isBlockedByCredits,
    recordingStatus,
    sendMessage,
    sendStopChatHotkeyRef,
    startRecording,
    stopRecording,
    cancelRecording,
    canUseVoiceMode,
    isVoiceModeActive,
    toggleVoiceMode,
    dictationMediaStreamRef,
    handleStopChat,
    isSendDisabled,
    canAttachFile,
    attachmentsCount,
    selectedProject,
    projectOptions,
    handleSelectProject,
    handleCreateProject,
    canQueueMessages,
    sendNowAction,
    handleSendNow,
    handleRemoveQueuedMessage,
    isSendingQueuedMessageNow,
  } = useComposer(props);
  const editorDisabled =
    isBlockedByCredits ||
    (!isVoiceModeActive && recordingStatus !== RecordingStatus.None);
  useFocusComposerOnType(inputRef, !editorDisabled);
  const composerBlocked = isBlockedByCredits;
  const { sendAskUserAnswersRef } = props;
  useLayoutEffect(() => {
    sendAskUserAnswersRef.current = sendMessage;
  }, [sendAskUserAnswersRef, sendMessage]);

  useEffect(() => {
    if (!composerBlocked) return;
    cancelRecording();
  }, [cancelRecording, composerBlocked]);

  const composerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const addSlotRef = useRef<HTMLDivElement>(null);
  const actionsSlotRef = useRef<HTMLDivElement>(null);
  const [editorSlotEl, setEditorSlotEl] = useState<HTMLDivElement | null>(null);
  const [isMultiline, setIsMultiline] = useState(false);
  const draftContent = draft?.content ?? '';

  const measuresAsMultiline = useCallback(() => {
    const editorEl = inputRef.current;
    const grid = gridRef.current;
    if (!editorEl || !grid) return false;
    const addWidth = addSlotRef.current?.offsetWidth ?? 0;
    const actionsWidth = actionsSlotRef.current?.offsetWidth ?? 0;
    const narrowWidth = grid.clientWidth - addWidth - actionsWidth;
    if (narrowWidth <= 0) return false;
    const previousWidth = editorEl.style.width;
    editorEl.style.width = `${narrowWidth - 4}px`;
    const height = editorEl.offsetHeight;
    editorEl.style.width = previousWidth;
    return height > AGENT_COMPOSER_MIN_HEIGHT + 8;
  }, [inputRef]);

  useLayoutEffect(() => {
    if (draftContent === '') {
      setIsMultiline(false);
      return;
    }
    setIsMultiline((prev) => prev || measuresAsMultiline());
  }, [draftContent, measuresAsMultiline]);

  useLayoutEffect(() => {
    if (!editorSlotEl) return;
    const sync = () => setIsMultiline((prev) => prev || measuresAsMultiline());
    const observer = new ResizeObserver(sync);
    observer.observe(editorSlotEl);
    if (gridRef.current) observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, [editorSlotEl, measuresAsMultiline]);

  const sendTooltipHotkey = useShortcutKeys(
    'chat.send',
    config.chat.require_mod_enter_to_send
      ? (['mod', 'enter'] as const)
      : (['enter'] as const)
  );

  const liveAskUserRequest =
    props.threadId === NEW_CHAT_ID
      ? null
      : messagesStore.getLiveAskUserRequest(props.threadId);
  const agent = agentsStore.getByChatId(props.threadId);
  const composerPlaceholder = liveAskUserRequest
    ? 'Add a note — answers send with your message'
    : agent
      ? `Message ${agent.name}`
      : 'Ask Littlebird';

  const { refetch: refetchSubscription } = useSubscription();

  const mergedInputRef = useMemo(
    () => mergeRefs(inputRef, blurInputHotkeyRef, sendStopChatHotkeyRef),
    [inputRef, blurInputHotkeyRef, sendStopChatHotkeyRef]
  ) as (element: HTMLElement | null) => void;

  const prevIsBusyRef = useRef(isBusy);
  const prevThreadStatusRef = useRef(thread?.status);
  const messageId = thread?.lastMessageId;

  useEffect(() => {
    if (prevIsBusyRef.current === true && isBusy === false) {
      const chatId = threadId;
      trackEvent('MESSAGE_COMPLETED', {
        message_id: messageId,
        chat_id: chatId,
      });
    }
    prevIsBusyRef.current = isBusy;
  }, [isBusy, messageId, threadId]);

  const selectedTexts = draft?.selectedTexts ?? [];
  const hasFiles = (draft?.files?.length ?? 0) > 0;
  const quotesScrollRef = useRef<HTMLDivElement>(null);
  const { maskStyle: quotesMaskStyle, suppressBottomFade } = useScrollFade(
    quotesScrollRef,
    { enabled: selectedTexts.length > 0 }
  );

  const suppressFadeDuringScroll = useCallback(
    () => suppressBottomFade(500),
    [suppressBottomFade]
  );
  useScrollToBottomOnGrow(
    quotesScrollRef,
    selectedTexts.length,
    suppressFadeDuringScroll
  );

  useEffect(() => {
    if (selectedTexts.length > 0 || hasFiles) inputRef.current?.focus();
  }, [selectedTexts.length, hasFiles, inputRef]);

  const isResearchMode =
    currentChatMode === RESEARCH_CHAT_MODE ||
    currentChatMode === ULTRA_CHAT_MODE ||
    userStore.user?.chat_mode_details?.[currentChatMode]?.response_mode ===
      'research';

  useEffect(() => {
    const currentStatus = thread?.status;
    if (
      currentStatus === ChatStatus.Streaming &&
      prevThreadStatusRef.current !== ChatStatus.Streaming
    ) {
      refetchSubscription();
    }
    prevThreadStatusRef.current = currentStatus;
  }, [thread?.status, refetchSubscription]);

  if (thread?.status === ChatStatus.Errored) {
    return (
      <RegenerateButton onPress={resendQuery} error={thread.errorMessage} />
    );
  }

  const hasCancelableActivity =
    isBusy || recordingStatus !== RecordingStatus.None;

  const draftIsEmpty = (draft?.content ?? '').trim().length === 0;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !draftIsEmpty) return;
      const target = event.target as HTMLElement | null;
      if (!target?.closest('[contenteditable="true"]')) return;
      event.preventDefault();
      void navigate({ to: Routes.AgentsNewTask as string });
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [draftIsEmpty, navigate]);

  return (
    <>
      {thread?.isLegacyChat && <LegacyChatWarning />}
      <OutOfCreditsComposerBanner />
      <LiteModeBanner />
      <motion.div
        ref={composerRef}
        layout
        transition={flipTransition}
        className={classList(
          'relative rounded-2xl',
          'smoothing-default',
          'cursor-[text]',
          'z-10',
          'border border-border-strong shadow-sm bg-surface p-2'
        )}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('button')) return;
          inputRef.current?.focus();
        }}
      >
        <YStack>
          <AnimatePresence>
            {isResearchMode && (
              <ResearchLimitWarning
                isNewThread={false}
                className="-mx-2 -mt-2 mb-3"
              />
            )}
            <UsageWarningNotification className="-mx-2 -mt-2 mb-3" />
          </AnimatePresence>
          {selectedTexts.length > 0 && (
            <div
              ref={quotesScrollRef}
              className={classList(
                'mb-2 flex flex-col gap-2',
                selectedTexts.length > 1 &&
                  'max-h-48 overflow-y-auto scrollbar-hide'
              )}
              style={quotesMaskStyle}
            >
              {selectedTexts.map((selection) => (
                <motion.div
                  key={selection.id}
                  layout
                  transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 40,
                    mass: 0.6,
                  }}
                  className={classList(
                    'flex items-start gap-2 shrink-0',
                    'bg-surface-active rounded-xl p-3',
                    'smoothing-default cursor-default overflow-hidden'
                  )}
                >
                  <YStack className="flex-1 gap-1">
                    <span className="text-xs text-foreground-subtle">
                      Selected text
                    </span>
                    <span className="text-sm text-foreground leading-relaxed line-clamp-2">
                      {selection.text}
                    </span>
                  </YStack>
                  <Tap>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      icon={Icons.cancel}
                      type="button"
                      onClick={() =>
                        draftsStore.removeSelectedText(draftId, selection.id)
                      }
                    />
                  </Tap>
                </motion.div>
              ))}
            </div>
          )}
          <AnimatePresence>
            {draft?.files && draft.files.length > 0 && (
              <motion.div
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto', filter: 'blur(0px)' }}
                exit={{ opacity: 0, height: 0, filter: 'blur(10px)' }}
                transition={{
                  opacity: { duration: 0.2 },
                  height: { type: 'spring', stiffness: 800, damping: 35 },
                }}
              >
                <FileGallery
                  attachedFiles={draft.files}
                  removeFile={(file) => draftsStore.removeFile(draftId, file)}
                  onSaveText={handleSavePastedText}
                  onInsertText={handleInsertPastedText}
                  isPreviewEnabled={true}
                />
              </motion.div>
            )}
          </AnimatePresence>
          {canQueueMessages && (
            <QueuedMessages
              threadId={threadId}
              sendNowAction={sendNowAction}
              isSendingNow={isSendingQueuedMessageNow}
              onSendNow={handleSendNow}
              onRemove={handleRemoveQueuedMessage}
            />
          )}
        </YStack>

        <div
          ref={gridRef}
          className={classList(
            'grid grid-cols-[auto_minmax(0,1fr)_auto]',
            isMultiline
              ? "[grid-template-areas:'editor_editor_editor'_'add_._actions'] gap-y-2 items-end"
              : "[grid-template-areas:'add_editor_actions'] gap-x-1 items-center"
          )}
        >
          <motion.div
            ref={addSlotRef}
            layout="position"
            transition={flipTransition}
            inert={isBlockedByCredits && !hasCancelableActivity}
            className={classList(
              'flex [grid-area:add] items-center',
              isBlockedByCredits && 'opacity-60'
            )}
          >
            <ComposerAddMenu
              draftId={draftId}
              triggerClassName="border-0 hover:bg-surface-hover"
              canAttachFile={canAttachFile}
              attachmentsCount={attachmentsCount}
              selectedProject={selectedProject}
              projectOptions={projectOptions}
              onAddFiles={handleFileSelection}
              onSelectProject={handleSelectProject}
              onCreateProject={handleCreateProject}
            />
            <HivemindPicker
              draftId={draftId}
              triggerClassName="border-0 hover:bg-surface-hover"
            />
          </motion.div>
          <motion.div
            ref={setEditorSlotEl}
            layout="position"
            transition={flipTransition}
            className="[grid-area:editor] relative flex min-w-0 items-center"
          >
            <AgentsComposerEditor
              expanded={isMultiline}
              inputRef={mergedInputRef}
              value={draft?.content ?? ''}
              document={draft?.draftDocument}
              placeholder={composerPlaceholder}
              autoFocus
              mentionsRef={mentionsRef}
              draftId={draftId}
              onChange={setDraftContent}
              onPaste={handlePaste}
              disabled={editorDisabled}
            />
          </motion.div>
          <motion.div
            ref={actionsSlotRef}
            layout="position"
            transition={flipTransition}
            inert={isBlockedByCredits && !hasCancelableActivity}
            data-composer-actions
            className={classList(
              'flex [grid-area:actions] justify-self-end items-center gap-2',
              isBlockedByCredits && 'opacity-60'
            )}
          >
            <ChatModeDropdown
              mode={currentChatMode}
              setMode={setCurrentChatMode}
              onRequestUpgrade={() => setHash('settings/Subscription')}
            />
            {isVoiceModeActive ? (
              <VoiceModeWaveform
                mediaStream={
                  recordingStatus === RecordingStatus.Recording
                    ? getVoiceModeMediaStream()
                    : null
                }
              />
            ) : (
              <MicrophoneButton
                mediaStreamRef={dictationMediaStreamRef}
                recordingStatus={recordingStatus}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
                onCancelRecording={cancelRecording}
                disabled={isBusy || isBlockedByCredits}
              />
            )}
            {canUseVoiceMode && (
              <VoiceModeButton
                isActive={isVoiceModeActive}
                onToggle={toggleVoiceMode}
                className="-ml-1.5"
                disabled={
                  !isVoiceModeActive &&
                  (isBlockedByCredits ||
                    isBusy ||
                    recordingStatus === RecordingStatus.Processing)
                }
              />
            )}
            <SendButton
              isBusy={isBusy}
              onSend={sendMessage}
              onStop={handleStopChat}
              disabled={isSendDisabled}
              tooltipHotkey={sendTooltipHotkey}
            />
          </motion.div>
        </div>
      </motion.div>
    </>
  );
});
